import type { ComponentType } from 'react';
import type { Cadence, MockSectionCustomerInput } from '@act/shared/data-transfer-objects';
import { SectionRenderer } from './SectionRenderer';
import HeroSection from './sections/HeroSection';
import EngagementStory from './sections/EngagementStory';
import VbpEngagement from './sections/VbpEngagement';
import PopulationProfile from './sections/PopulationProfile';
import WhatMembersNeed from './sections/WhatMembersNeed';
import ConnectionsBehavioralCare, {
  ConnectionsBehavioralCarePageThree,
} from './sections/ConnectionsBehavioralCare';
import BhMedicalDiagnoses from './sections/BhMedicalDiagnoses';
import ClinicalCareOutcomes from './sections/ClinicalCareOutcomes';
import ClosingValue from './sections/ClosingValue';
import LookingAhead from './sections/LookingAhead';
import WhatsPossibleAtMaturity from './sections/WhatsPossibleAtMaturity';
import ConnectionsPrimaryCare, {
  ConnectionsPrimaryCarePageTwo,
} from './sections/ConnectionsPrimaryCare';
import ConnectionsSection from './sections/ConnectionsSection';
import MeasureProjections from './sections/MeasureProjections';
import HarpEnrollment from './sections/HarpEnrollment';
import GapClosures from './sections/GapClosures';
import HomeVisits from './sections/HomeVisits';
import MemberStory from './sections/MemberStory';
import type { ReportReadingColumnItem } from './reportPreview.adapter.util';
import type { ReportHostedSectionProps } from './reportHostedSection.types';
import type {
  ReportInsightCardKey,
  ReportInsightFieldKey,
} from './ReportInsightPair';
import { getSectionMeta } from '../utils/reportDisplay.util';
import { ReportSectionAuthoringBlocks } from './ReportSectionAuthoringBlocks';
import { ReportSectionInsightBlocks } from './ReportSectionInsightBlocks';

const HOSTED_COMPONENTS: Record<string, ComponentType<ReportHostedSectionProps>> = {
  hero: HeroSection,
  'engagement-story': EngagementStory,
  'vbp-engagement': VbpEngagement,
  'who-were-reaching': PopulationProfile,
  'what-members-need': WhatMembersNeed,
  'connections-behavioral-care': ConnectionsBehavioralCare,
  'bh-medical-diagnoses': BhMedicalDiagnoses,
  'clinical-care-outcomes': ClinicalCareOutcomes,
  'connections-primary-care': ConnectionsPrimaryCare,
  'connections-that-matter': ConnectionsSection,
  'quality-measure-projections': MeasureProjections,
  'harp-enrollment': HarpEnrollment,
  'gap-closures': GapClosures,
  'home-visits': HomeVisits,
  'member-spotlight': MemberStory,
  'closing-value': ClosingValue,
  'whats-possible-at-maturity': WhatsPossibleAtMaturity,
  'looking-ahead': LookingAhead,
};

/** Hero keeps its callout inside the navy cover; other sections use the tail block. */
const HOSTED_SECTIONS_WITHOUT_BOTTOM_CALLOUT = new Set<string>(['hero']);

const DECOR_FRAGMENTS: Record<string, string> = {};

export type ReportSectionItemRenderMode = 'preview' | 'published';

export interface ReportSectionItemViewProps {
  item: ReportReadingColumnItem;
  customer: MockSectionCustomerInput;
  reportingPeriod: string;
  measurementPeriod?: string;
  mode: ReportSectionItemRenderMode;
  reportId?: string;
  cadence?: Cadence;
  authoringEditable?: boolean;
  /** @param value - Updated callout from inline edit. */
  onCalloutChange?: (value: string) => void;
  /** @param value - Updated callout title from inline edit. */
  onCalloutTitleChange?: (value: string) => void;
  /**
   * @param card - Clinical or financial insight card.
   * @param field - Overline, heading, or body field.
   * @param value - Updated insight text while typing.
   */
  onInsightFieldChange?: (
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
  /**
   * @param card - Clinical or financial insight card.
   * @param field - Overline, heading, or body field.
   * @param value - Final insight text after blur.
   */
  onInsightFieldBlur?: (
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
  /**
   * @param dataSnapshot - Full CSM-authored snapshot JSON while typing.
   */
  onDataSnapshotChange?: (dataSnapshot: Record<string, unknown>) => void;
  /**
   * @param dataSnapshot - Full CSM-authored snapshot JSON after blur.
   */
  onDataSnapshotBlur?: (dataSnapshot: Record<string, unknown>) => void;
}

/**
 * Renders one report section with the same logic as {@link ReportReadingColumn}
 * (hosted Studio components, HTML decor slabs, or SectionRenderer fallback).
 * @param props - Resolved snapshot row and customer context.
 */
export const ReportSectionItemView = ({
  item,
  customer,
  reportingPeriod,
  measurementPeriod,
  mode,
  reportId,
  cadence,
  authoringEditable = false,
  onCalloutChange,
  onCalloutTitleChange,
  onInsightFieldChange,
  onInsightFieldBlur,
  onDataSnapshotChange,
  onDataSnapshotBlur,
}: ReportSectionItemViewProps) => {
  const bottomCallout =
    !HOSTED_SECTIONS_WITHOUT_BOTTOM_CALLOUT.has(item.sectionId) ? (
      <ReportSectionAuthoringBlocks
        band
        sectionId={item.sectionId}
        callout={item.callout}
        calloutTitle={item.calloutTitle}
        calloutEnabled={item.calloutEnabled}
        editable={authoringEditable}
        onCalloutChange={onCalloutChange}
        onCalloutTitleChange={onCalloutTitleChange}
      />
    ) : null;

  const bottomInsights = (
    <ReportSectionInsightBlocks
      sectionId={item.sectionId}
      dataSnapshot={item.dataSnapshot}
      sectionInsights={item.insights}
      clinicalInsightEnabled={item.clinicalInsightEnabled}
      financialInsightEnabled={item.financialInsightEnabled}
      editable={authoringEditable}
      onInsightFieldChange={onInsightFieldChange}
      onInsightFieldBlur={onInsightFieldBlur}
    />
  );

  const Hosted = HOSTED_COMPONENTS[item.sectionId];
  if (Hosted) {
    const routesBhTailOnPageThree =
      item.sectionId === 'connections-behavioral-care' &&
      (item.calloutEnabled || item.clinicalInsightEnabled || item.financialInsightEnabled);
    const hostedProps: ReportHostedSectionProps = {
      data: item.dataSnapshot ?? {},
      customer,
      reportingPeriod,
      measurementPeriod,
      reportId,
      cadence,
      callout: item.callout,
      calloutTitle: item.calloutTitle,
      calloutEnabled: item.calloutEnabled,
      heroKpiVisibility: item.heroKpiVisibility,
      authoringEditable,
      onCalloutChange,
      onCalloutTitleChange,
      onDataSnapshotChange,
      onDataSnapshotBlur,
    };
    if (item.sectionId === 'connections-behavioral-care') {
      const bhTail =
        routesBhTailOnPageThree ? (
          <>
            {bottomCallout}
            {bottomInsights}
          </>
        ) : undefined;
      return (
        <>
          <Hosted {...hostedProps} />
          <ConnectionsBehavioralCarePageThree
            data={item.dataSnapshot ?? {}}
            tail={bhTail}
          />
        </>
      );
    }
    if (item.sectionId === 'connections-primary-care') {
      const routesPcpTailOnPageTwo =
        item.calloutEnabled ||
        item.clinicalInsightEnabled ||
        item.financialInsightEnabled;
      const pcpTail =
        routesPcpTailOnPageTwo ? (
          <>
            {bottomCallout}
            {bottomInsights}
          </>
        ) : undefined;
      return (
        <>
          <Hosted {...hostedProps} />
          <ConnectionsPrimaryCarePageTwo
            data={item.dataSnapshot ?? {}}
            tail={pcpTail}
          />
        </>
      );
    }
    return (
      <>
        <Hosted {...hostedProps} />
        {bottomCallout}
        {bottomInsights}
      </>
    );
  }

  const decor = DECOR_FRAGMENTS[item.sectionId];
  if (decor) {
    return (
      <>
        <div
          className="report-hosted-html-section"
          data-clinical-insight-enabled={String(item.clinicalInsightEnabled)}
          data-financial-insight-enabled={String(item.financialInsightEnabled)}
          dangerouslySetInnerHTML={{ __html: decor }}
        />
        {bottomCallout}
        {bottomInsights}
      </>
    );
  }

  const meta = getSectionMeta(item.sectionId);
  if (!meta) {
    return null;
  }

  return (
    <section className="section section--white">
      <div className="container">
        <SectionRenderer
          section={meta}
          reportSection={{
            sectionId: item.sectionId,
            calloutEnabled: item.calloutEnabled,
            narrative: item.narrative,
            callout: item.callout,
            calloutTitle: item.calloutTitle,
            dataSnapshot: item.dataSnapshot,
          }}
          customer={customer}
          reportingPeriod={reportingPeriod}
          mode={mode}
        />
        <ReportSectionAuthoringBlocks
          sectionId={item.sectionId}
          callout={item.callout}
          calloutTitle={item.calloutTitle}
          calloutEnabled={item.calloutEnabled}
          editable={authoringEditable}
          onCalloutChange={onCalloutChange}
          onCalloutTitleChange={onCalloutTitleChange}
        />
      </div>
    </section>
  );
};
